import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, Col, Row } from 'react-bootstrap';
import MenuItem from '@mui/material/MenuItem'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/treatment-plans/treatment-plans/style'

/** API */
import TreatmentPlanApi from 'api/setting/treatment-plans/treatment-plan.api'

/** COMPONENT */
import { routeName } from 'routes/routes-name'
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import ButtonExport from 'component/Button/ButtonExport'
import TableRowExport from 'component/Pdf/TableRowExport'
import AlertModal from 'new-components/modal/alert-modal';
import SnackbarComponent from 'new-components/snackbar/snackbar-component';

export default function TreatmentPlans() {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()

  const [treatmentPlans, setTreatmentPlans] = useState([])
  const [exportData, setExportData] = useState([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSort] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sort: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await TreatmentPlanApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setTreatmentPlans(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const loadDataForExport = useCallback(async () => {
    let condition: any = {}
    condition.export = 1
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sort: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await TreatmentPlanApi.findAll(condition)
    if (res.status === 200) {
      setExportData(res.data)
      setRowCount(res.headers['x-total'])
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadDataForExport()
  }, [loadDataForExport])

  const onCreate = () => {
    history.push(routeName.treatmentPlans + '/create')
  }

  const onActive = () => {
    if (id) {
      TreatmentPlanApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('TREATMENT_PLAN.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('TREATMENT_PLAN.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      TreatmentPlanApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('TREATMENT_PLAN.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('TREATMENT_PLAN.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (objData: any) => {

    history.push(routeName.treatmentPlans + '/update', objData)
  }

  const onRequestSort = (sortByVal: string, sort: string) => {
    setSort(sort)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'treatmentPlanTypeName', disablePadding: false, label: t('TREATMENT_PLAN.TABLE.CELL.TREATMENT_PLAN_TYPE') },
    { id: 'treatmentPlanName', disablePadding: false, label: t('TREATMENT_PLAN.TABLE.CELL.TREATMENT_PLAN_NAME') },
    { id: 'status', disablePadding: false, label: t('TREATMENT_PLAN.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('TREATMENT_PLAN.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { treatmentPlanId, treatmentPlanName, treatmentPlanTypeName, updatedBy, updatedAt, status: txPlanStatus } = objData
    const statusBtnActive = txPlanStatus === 'ACTIVE' ? true : false
    const statusBtnInActive = txPlanStatus === 'INACTIVE' ? true : false
    const objRenderData = {
      key: treatmentPlanId,
      id: treatmentPlanId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: treatmentPlanTypeName || "-" },
        { option: 'TEXT', align: 'left', label: treatmentPlanName || "-" },
        { option: 'STATUS', align: 'center', label: txPlanStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: false },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: false }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.treatmentPlanName)
  }
  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}` }
  ]

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`TREATMENT_PLAN.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`TREATMENT_PLAN.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { treatmentPlanName: name, status: t(`TREATMENT_PLAN.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('TREATMENT_PLAN.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={7}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('TREATMENT_PLAN.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={5} className="mt-2 mt-md-auto">
                <div className="pr-xl-2 d-flex">
                  <div className='ml-auto'>
                    <ButtonExport headCells={headCells} portrait={true} filter={filter} fileName={t('TREATMENT_PLAN.LIST_TREATMENT_PLAN_NAME')}
                      rowsData={exportData.map((val, i) => {
                        return renderData(val, i, true)
                      })} />
                  </div>
                  <div className='pl-3'>
                    <ButtonCustom mode="add" onClick={() => onCreate()} textButton={t('TREATMENT_PLAN.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" btnStyle={{ height: '44px' }} />
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={treatmentPlans.map((val, i) => {
                return renderData(val, i, false)
              })}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
